import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router';
import { Layout } from '../components/Layout';
import { ProfilePictureUpload } from '../components/ProfilePictureUpload';
import { ImportStudentsModal } from '../components/ImportStudentsModal';
import { studentsApi } from '../services/api';
import { Student } from '../types';

const PAGE_SIZE = 20;

export const Students = () => {
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedClass, setSelectedClass] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortBy, setSortBy] = useState<'added' | 'name'>('added');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [availableClasses, setAvailableClasses] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingStudent, setEditingStudent] = useState<Student | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    phone_number: '',
    date_of_birth: '',
    class: '',
    school: ''
  });
  const [profilePicture, setProfilePicture] = useState<File | null>(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState<string>('');

  useEffect(() => {
    fetchStudents();
  }, []);

  useEffect(() => { setCurrentPage(1); }, [selectedClass, searchQuery]);

  const filteredStudents = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return students
      .filter((s) => {
        const matchesClass = !selectedClass || s.class === selectedClass;
        const matchesSearch = !q ||
          s.name.toLowerCase().includes(q) ||
          (s.phone_number || '').toLowerCase().includes(q);
        return matchesClass && matchesSearch;
      })
      .sort((a, b) => {
        const cmp = sortBy === 'name'
          ? a.name.localeCompare(b.name)
          : a.created_at.localeCompare(b.created_at);
        return sortDir === 'asc' ? cmp : -cmp;
      });
  }, [students, selectedClass, searchQuery, sortBy, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filteredStudents.length / PAGE_SIZE));
  const pagedStudents = filteredStudents.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const response = await studentsApi.getAll();
      setStudents(response.data);

      // Extract unique classes for filter dropdown
      const classes = [...new Set(response.data.map(student => student.class).filter((c): c is string => c !== null))].sort();
      setAvailableClasses(classes);
    } catch (_err) {
      setError('Failed to fetch students');
    } finally {
      setLoading(false);
    }
  };

  const handleAddStudent = () => {
    setEditingStudent(null);
    setFormData({
      name: '',
      phone_number: '',
      date_of_birth: '',
      class: '',
      school: ''
    });
    setProfilePicture(null);
    setProfilePicturePreview('');
    setShowModal(true);
  };

  const handleEditStudent = (student: Student) => {
    setEditingStudent(student);
    setFormData({
      name: student.name,
      phone_number: student.phone_number || '',
      date_of_birth: student.date_of_birth ? student.date_of_birth.split('T')[0] : '', // Format for input[type="date"]
      class: student.class || '',
      school: student.school || ''
    });
    setProfilePicture(null);
    setProfilePicturePreview('');
    setShowModal(true);
  };

  const handleDeleteStudent = async (id: number) => {
    if (!confirm('Are you sure you want to delete this student?')) return;

    try {
      await studentsApi.delete(id);
      const updatedStudents = students.filter(s => s.id !== id);
      setStudents(updatedStudents);

      // Update available classes
      const classes = [...new Set(updatedStudents.map(student => student.class).filter((c): c is string => c !== null))].sort();
      setAvailableClasses(classes);

      // Reset filter if no students in selected class anymore
      if (selectedClass && !classes.includes(selectedClass)) {
        setSelectedClass('');
      }
    } catch (_err) {
      setError('Failed to delete student');
    }
  };

  const handleProfilePictureChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setProfilePicture(file);
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfilePicturePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeProfilePicture = () => {
    setProfilePicture(null);
    setProfilePicturePreview('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      let updatedStudents;
      if (editingStudent) {
        // Update existing student
        const response = await studentsApi.update(editingStudent.id, formData);
        updatedStudents = students.map(s =>
          s.id === editingStudent.id ? response.data : s
        );
      } else {
        // Create new student
        const response = await studentsApi.create(formData);
        let newStudent = response.data;

        // Upload profile picture if provided
        if (profilePicture) {
          try {
            const uploadResponse = await studentsApi.updateProfilePicture(newStudent.id, profilePicture);
            newStudent = { ...newStudent, profile_picture: uploadResponse.data.profile_picture };
          } catch (uploadErr) {
            console.error('Failed to upload profile picture:', uploadErr);
            // Continue without failing the entire operation
          }
        }

        updatedStudents = [...students, newStudent];
      }

      setStudents(updatedStudents);

      // Update available classes
      const classes = [...new Set(updatedStudents.map(student => student.class).filter((c): c is string => c !== null))].sort();
      setAvailableClasses(classes);

      setShowModal(false);
      setError('');
    } catch (err) {
      console.error('Student creation error:', err);
      const axErr = err as { response?: { data?: { error?: string; details?: { msg: string }[] } } };
      const errorMessage = axErr.response?.data?.error || 'Failed to save student';
      const errorDetails = axErr.response?.data?.details;

      if (errorDetails && Array.isArray(errorDetails)) {
        const detailMessages = errorDetails.map((detail) => detail.msg).join(', ');
        setError(`${errorMessage}: ${detailMessages}`);
      } else {
        setError(errorMessage);
      }
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <div className="text-lg">Loading students...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Students Management</h1>
          <div className="flex gap-3">
            <button
              onClick={() => setShowImportModal(true)}
              className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-medium"
            >
              Import
            </button>
            <button
              onClick={handleAddStudent}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium"
            >
              Add Student
            </button>
          </div>
        </div>

        {/* Filter Section */}
        <div className="mb-6 flex flex-wrap items-center gap-4">
          <div className="relative">
            <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search by name or phone..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-64"
            />
          </div>

          <div className="flex items-center space-x-2">
            <label htmlFor="class-filter" className="text-sm font-medium text-gray-700">
              Class:
            </label>
            <select
              id="class-filter"
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="">All Classes</option>
              {availableClasses.map((className) => (
                <option key={className} value={className}>
                  {className}
                </option>
              ))}
            </select>
          </div>

          {(selectedClass || searchQuery) && (
            <button
              onClick={() => { setSelectedClass(''); setSearchQuery(''); }}
              className="text-sm text-blue-600 hover:text-blue-800 underline"
            >
              Clear filters
            </button>
          )}

          <span className="ml-auto text-sm text-gray-500">
            {filteredStudents.length} of {students.length} students
          </span>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Profile
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    onClick={() => {
                      if (sortBy === 'name') {
                        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
                      } else {
                        setSortBy('name');
                        setSortDir('asc');
                      }
                    }}
                    className="flex items-center gap-1 uppercase tracking-wider hover:text-gray-700"
                  >
                    Name
                    {sortBy === 'name' && <span>{sortDir === 'asc' ? '▲' : '▼'}</span>}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Phone
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date of Birth
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Class
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  School
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {pagedStudents.map((student) => (
                <tr key={student.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center">
                      {student.profile_picture ? (
                        <img
                          src={`${student.profile_picture}`}
                          alt={`${student.name} profile`}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            e.currentTarget.parentElement!.innerHTML = '<div class="text-gray-400 text-xs">No Image</div>';
                          }}
                        />
                      ) : (
                        <div className="text-gray-400 text-xs">No Image</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Link
                      to={`/students/${student.id}`}
                      className="text-blue-600 hover:text-blue-800 font-medium"
                    >
                      {student.name}
                    </Link>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-900">
                    {student.phone_number || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-900">
                    {student.date_of_birth ? new Date(student.date_of_birth).toLocaleDateString() : '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-900">
                    {student.class || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-900">
                    {student.school || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <button
                      onClick={() => handleEditStudent(student)}
                      className="text-indigo-600 hover:text-indigo-900 mr-4"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteStudent(student.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredStudents.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">
                {students.length === 0 ? 'No students found.' : 'No students match your search.'}
              </p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4">
            <span className="text-sm text-gray-600">
              Page {currentPage} of {totalPages}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="px-2 py-1 text-sm rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50"
              >
                «
              </button>
              <button
                onClick={() => setCurrentPage((p) => p - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50"
              >
                Prev
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((p) => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                .reduce<(number | '...')[]>((acc, p, idx, arr) => {
                  if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push('...');
                  acc.push(p);
                  return acc;
                }, [])
                .map((item, idx) =>
                  item === '...' ? (
                    <span key={`ellipsis-${idx}`} className="px-2 py-1 text-sm text-gray-400">…</span>
                  ) : (
                    <button
                      key={item}
                      onClick={() => setCurrentPage(item as number)}
                      className={`px-3 py-1 text-sm rounded border ${currentPage === item ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300 hover:bg-gray-50'}`}
                    >
                      {item}
                    </button>
                  )
                )}
              <button
                onClick={() => setCurrentPage((p) => p + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50"
              >
                Next
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-2 py-1 text-sm rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50"
              >
                »
              </button>
            </div>
          </div>
        )}

        {/* Import Modal */}
        {showImportModal && (
          <ImportStudentsModal
            onClose={() => setShowImportModal(false)}
            onImported={(newStudents) => {
              const updatedStudents = [...students, ...newStudents];
              setStudents(updatedStudents);
              const classes = [...new Set(updatedStudents.map(s => s.class).filter((c): c is string => c !== null))].sort();
              setAvailableClasses(classes);
            }}
          />
        )}

        {/* Modal for Add/Edit Student */}
        {showModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" onClick={() => setShowModal(false)}>
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white" onClick={(e) => e.stopPropagation()}>
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {editingStudent ? 'Edit Student' : 'Add New Student'}
                </h3>
                {!editingStudent && (
                  <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
                    <p className="text-sm text-blue-800">
                      <strong>Required:</strong> Name and Phone Number<br/>
                      <strong>Optional:</strong> Date of Birth, Class, and School can be added later
                    </p>
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      required
                      value={formData.phone_number}
                      onChange={(e) => setFormData({...formData, phone_number: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Will be used as username for student login"
                    />
                    {!editingStudent && (
                      <p className="mt-1 text-xs text-gray-500">
                        A user account will be automatically created with this phone number as username and default password "123456"
                      </p>
                    )}
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Date of Birth
                    </label>
                    <input
                      type="date"
                      value={formData.date_of_birth}
                      onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Class
                    </label>
                    <input
                      type="text"
                      value={formData.class}
                      onChange={(e) => setFormData({...formData, class: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., Grade 10A, Year 12, etc."
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      School
                    </label>
                    <input
                      type="text"
                      value={formData.school}
                      onChange={(e) => setFormData({...formData, school: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="e.g., Lincoln High School, St. Mary's Academy, etc."
                    />
                  </div>

                  {/* Profile Picture Upload - for adding new students */}
                  {!editingStudent && (
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Profile Picture (Optional)
                      </label>
                      <div className="flex items-center space-x-4">
                        {profilePicturePreview ? (
                          <div className="relative">
                            <img
                              src={profilePicturePreview}
                              alt="Profile preview"
                              className="w-16 h-16 rounded-full object-cover border-2 border-gray-300"
                            />
                            <button
                              type="button"
                              onClick={removeProfilePicture}
                              className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600"
                            >
                              ×
                            </button>
                          </div>
                        ) : (
                          <div className="w-16 h-16 rounded-full bg-gray-100 border-2 border-dashed border-gray-300 flex items-center justify-center">
                            <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                          </div>
                        )}
                        <div className="flex-1">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleProfilePictureChange}
                            className="hidden"
                            id="profile-picture-input"
                          />
                          <label
                            htmlFor="profile-picture-input"
                            className="cursor-pointer bg-white py-2 px-3 border border-gray-300 rounded-md shadow-sm text-sm leading-4 font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            Choose Image
                          </label>
                          <p className="mt-1 text-xs text-gray-500">
                            PNG, JPG up to 10MB
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Profile Picture Upload - only for editing existing students */}
                  {editingStudent && (
                    <div className="mb-6 border-t pt-6">
                      <ProfilePictureUpload
                        studentId={editingStudent.id}
                        currentPicture={editingStudent.profile_picture}
                        onUploadSuccess={(newPicturePath) => {
                          // Update the editing student and the students list
                          const updatedStudent = { ...editingStudent, profile_picture: newPicturePath };
                          setEditingStudent(updatedStudent);
                          setStudents(students.map(s => s.id === editingStudent.id ? updatedStudent : s));
                        }}
                        onDeleteSuccess={() => {
                          // Update the editing student and the students list
                          const updatedStudent = { ...editingStudent, profile_picture: null };
                          setEditingStudent(updatedStudent);
                          setStudents(students.map(s => s.id === editingStudent.id ? updatedStudent : s));
                        }}
                      />
                    </div>
                  )}

                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                    >
                      {editingStudent ? 'Update' : 'Create'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};
